import React, { useEffect } from "react";
import Header from "../components/Header.js";
import Footer from "../components/Footer.js";
import { useLocation } from "react-router-dom";
import {
  BarmaSorigVideoAdvertisement,
  videoThumbnail,
  founder,
  generalManager,
  generalSupervisor,
  staff01,
  staff02,
} from "../utils/awsUrls.js";
import { scrollToHash } from "../utils/scrollUtils.js";

function AboutPage() {
  const location = useLocation();

  useEffect(() => {
    // Scrolling Behaviour when clicked on submenu containing id
    scrollToHash(location.hash);
  }, [location]);

  return (
    <>
      <Header />
      <div
        className="about-page bg-gradient-to-br from-gray-50 via-blue-50/30 to-white"
        id="barma-sorig"
      >
        {/* Enhanced Hero Section */}
        <section className="hero relative bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 text-white py-12 overflow-hidden">
          <div className="container mx-auto px-6 text-center relative z-10">
            <h1 className="text-6xl md:text-7xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-white via-blue-100 to-sky-200 drop-shadow-2xl animate-fade-in">
              About Us
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed text-blue-100 font-light animate-fade-in delay-300">
              Discover our mission, values, and the passionate team behind our
              success.
            </p>
          </div>
        </section>

        {/* Enhanced About Section */}
        <section className="relative py-24 bg-gradient-to-br from-white via-gray-50/50 to-blue-50/30 overflow-hidden">
          <div className="container mx-auto px-6 lg:px-12 relative z-10">
            <div className="flex flex-col lg:flex-row items-center gap-16">
              {/* Enhanced Video Advertisement with Caption Below */}
              <div className="lg:w-1/2 group">
                <div className="relative">
                  {/* Glowing ring effect */}
                  <div className="absolute -inset-4 bg-gradient-to-r from-blue-400 via-sky-500 to-blue-600 rounded-2xl opacity-20 blur-xl group-hover:opacity-40 transition-all duration-500 animate-pulse"></div>

                  <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                    <video
                      controls
                      poster={videoThumbnail}
                      className="w-full h-auto rounded-2xl"
                      preload="metadata"
                    >
                      <source
                        src={BarmaSorigVideoAdvertisement}
                        type="video/mp4"
                      />
                      Your browser does not support the video tag.
                    </video>
                  </div>

                  {/* Floating particles */}
                  <div className="absolute -top-2 -right-2 w-4 h-4 bg-blue-400 rounded-full animate-ping opacity-75"></div>
                  <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-sky-400 rounded-full animate-ping opacity-75 delay-500"></div>
                </div>

                {/* Enhanced Caption Below Video */}
                <div className="mt-6 p-6 bg-gradient-to-br from-white via-gray-50 to-blue-50/50 rounded-2xl shadow-xl border border-blue-100 backdrop-blur-sm">
                  <h3 className="text-2xl lg:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-800 to-blue-700 mb-2">
                    Discover Barma Sorig Healing
                  </h3>
                  <p className="text-gray-700 text-base lg:text-lg leading-relaxed font-medium">
                    Experience our dedicated staff, serene location, and
                    holistic therapies designed to restore your body, mind, and
                    spirit.
                  </p>
                </div>
              </div>

              {/* Enhanced Content */}
              <div className="lg:w-1/2">
                <h2 className="text-5xl lg:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-gray-800 via-blue-700 to-gray-800 mb-8 leading-tight drop-shadow-lg">
                  Who We Are
                </h2>
                <p className="text-gray-700 text-xl leading-relaxed mb-8 font-medium">
                  Since 2016,{" "}
                  <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-800">
                    Pure Vision Sorig Healing & Research Center, Nepal
                  </span>
                  , and{" "}
                  <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-700">
                    Barma Sorig Healing Center, Bhutan
                  </span>{" "}
                  have been dedicated to sharing the ancient wisdom of Tibetan
                  Sowa-Rigpa with guests of all ages. Rooted in centuries-old
                  tradition, our therapies bring relaxation, healing, and
                  balance to body, mind, and spirit.
                </p>

                {/* Enhanced Highlights with icons */}
                <div className="space-y-8">
                  <div className="group flex items-start p-4 rounded-2xl hover:bg-green-50/50 transition-all duration-300 hover:shadow-lg">
                    <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center rounded-full bg-gradient-to-br from-green-100 to-emerald-200 text-green-600 text-2xl mr-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <i className="fas fa-spa"></i>
                    </div>
                    <p className="text-gray-700 text-lg leading-relaxed font-medium">
                      <span className="font-bold text-green-700">
                        Five Nectars Herbal Steam
                      </span>{" "}
                      — detoxifying, rebalancing, and revitalizing the body and
                      mind.
                    </p>
                  </div>

                  <div className="group flex items-start p-4 rounded-2xl hover:bg-yellow-50/50 transition-all duration-300 hover:shadow-lg">
                    <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center rounded-full bg-gradient-to-br from-yellow-100 to-amber-200 text-yellow-600 text-2xl mr-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <i className="fas fa-hands"></i>
                    </div>
                    <p className="text-gray-700 text-lg leading-relaxed font-medium">
                      <span className="font-bold text-amber-700">
                        Jyamtse Ku-Nye Massage
                      </span>{" "}
                      — a traditional therapy using herbal oils and sacred
                      stones to restore harmony of the three humors (Wind, Bile
                      & Phlegm).
                    </p>
                  </div>
                </div>

                {/* Enhanced Closing statement */}
                <p className="mt-10 text-gray-700 text-xl leading-relaxed font-medium p-6 bg-gradient-to-r from-blue-50/50 to-sky-50/50 rounded-2xl border-l-4 border-blue-500 shadow-lg">
                  Our Bhutanese therapists are fully trained, deeply committed,
                  and bring years of experience to every session. Whether you
                  seek relaxation, preventative care, or support for specific
                  conditions, our center is here to guide you on a journey of
                  healing and renewal.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Enhanced Mission & Vision Section */}
        <section
          id="mission-vision"
          className="mission-section relative bg-gradient-to-br from-blue-100 via-sky-100 to-blue-200 py-24 overflow-hidden"
        >
          <div className="container mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-16 text-center relative z-10">
            {/* Enhanced Left Section: Mission content */}
            <div className="mission-content group">
              <div className="p-8 bg-white/70 backdrop-blur-sm rounded-3xl shadow-2xl border border-blue-200">
                <div className="mb-6">
                  <div className="w-20 h-20 mx-auto bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white text-3xl shadow-xl group-hover:scale-110 transition-transform duration-300">
                    <i className="fas fa-bullseye"></i>
                  </div>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-800 mb-8 drop-shadow-lg">
                  Our Mission
                </h2>
                <p className="text-gray-700 leading-relaxed text-lg lg:text-xl font-medium">
                  To promote & preserve our traditional Sowa-Rigpa Ku Nye
                  tradtion and its practices.
                </p>
              </div>
            </div>

            {/* Enhanced Vertical Separator - visible only on md and larger screen */}
            <div className="hidden md:block absolute left-1/2 top-8 bottom-8 w-1 bg-gradient-to-b from-transparent via-blue-400 to-transparent transform -translate-x-1/2 rounded-full"></div>

            {/* Enhanced Right Section: Vision content */}
            <div className="vision-content group">
              <div className="p-8 bg-white/70 backdrop-blur-sm rounded-3xl shadow-2xl border border-blue-200">
                <div className="mb-6">
                  <div className="w-20 h-20 mx-auto bg-gradient-to-br from-indigo-500 to-purple-700 rounded-full flex items-center justify-center text-white text-3xl shadow-xl group-hover:scale-110 transition-transform duration-300">
                    <i className="fas fa-eye"></i>
                  </div>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-800 mb-8 drop-shadow-lg">
                  Our Vision
                </h2>
                <p className="text-gray-700 leading-relaxed text-lg lg:text-xl font-medium">
                  To provide necessary traditional Health Care Service, and to
                  improve the mental, emotional &amp; physical well-being of
                  all, while strengthening the traditional Health Care System of
                  Our Country, Bhutan.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Enhanced Team Section */}
        <section
          id="staff"
          className="team-section relative bg-gradient-to-br from-blue-50 via-sky-50/50 to-white py-24 overflow-hidden"
        >
          <div className="container mx-auto px-6 relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-sky-600 to-blue-800 mb-4 drop-shadow-2xl">
                Meet Our Team
              </h2>
              <div className="w-32 h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent rounded-full mx-auto animate-pulse"></div>
            </div>

            {/* Founder/CEO Highlight */}
            <div className="flex justify-center mb-16">
              <div className="group team-member text-center bg-gradient-to-br from-white via-blue-50/30 to-white shadow-2xl rounded-3xl p-10 w-full max-w-md border border-blue-100">
                <div className="relative mb-8">
                  <div className="absolute -inset-4 bg-gradient-to-r from-blue-400 via-sky-500 to-blue-600 rounded-full opacity-20 blur-xl group-hover:opacity-40 transition-all duration-500 animate-pulse"></div>
                  <img
                    src={founder}
                    alt="Dr. Sherab Tenzin"
                    className="relative rounded-full mx-auto w-40 h-40 object-cover border-4 border-blue-600 shadow-2xl group-hover:border-blue-500 transition-all duration-300"
                  />
                  <div className="absolute -top-2 -right-2 w-4 h-4 bg-blue-400 rounded-full animate-ping opacity-75"></div>
                  <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-sky-400 rounded-full animate-ping opacity-75 delay-300"></div>
                </div>
                <h3 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-800 to-blue-700 mb-2">
                  Dr. Sherab Tenzin
                </h3>
                <p className="text-blue-600 font-semibold text-lg mb-2">
                  Founder / CEO
                </p>
                <p className="text-gray-600 text-base font-medium">
                  Visionary leader and founder, dedicated to holistic healing
                  and Sowa-Rigpa tradition.
                </p>
              </div>
            </div>

            {/* Other Team Members */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
              {[
                {
                  name: "Sangay Dorji",
                  role: "General Manager",
                  img: generalManager,
                  desc: "Ensures smooth operations and guest satisfaction with professionalism and care.",
                },
                {
                  name: "Dechen Pelden",
                  role: "General Supervisor / Senior Ku Nye Therapist",
                  img: generalSupervisor,
                  desc: "Expert in Ku Nye therapies, guiding the team and clients to wellness.",
                },
                {
                  name: "Sonam Yangzom",
                  role: "Ku Nye Therapist",
                  img: staff01,
                  desc: "Specializes in traditional massage therapies for holistic health.",
                },
                {
                  name: "Damchoe",
                  role: "Ku Nye Therapist",
                  img: staff02,
                  desc: "Brings compassion and skill to every healing session.",
                },
              ].map((member, index) => (
                <div
                  key={index}
                  className="group team-member text-center bg-gradient-to-br from-white via-blue-50/30 to-white shadow-2xl rounded-3xl p-8 transform border border-blue-100"
                >
                  <div className="relative mb-6">
                    <div className="absolute -inset-3 bg-gradient-to-r from-blue-400 via-sky-500 to-blue-600 rounded-full opacity-15 blur-lg group-hover:opacity-30 transition-all duration-500"></div>
                    <img
                      src={member.img}
                      alt={member.name}
                      className="relative rounded-full mx-auto w-32 h-32 object-cover border-4 border-blue-600 shadow-xl group-hover:border-blue-500 transition-all duration-300"
                    />
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-400 rounded-full animate-ping opacity-75 delay-200"></div>
                    <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-sky-400 rounded-full animate-ping opacity-75 delay-700"></div>
                  </div>
                  <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-800 to-blue-700 mb-1">
                    {member.name}
                  </h3>
                  <p className="text-blue-600 font-semibold mb-2">
                    {member.role}
                  </p>
                  <p className="text-gray-600 text-sm">{member.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
      <Footer />

      {/* Custom animations */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }

        .delay-300 {
          animation-delay: 0.3s;
        }

        @media (prefers-reduced-motion: reduce) {
          .animate-pulse,
          .animate-ping,
          .animate-fade-in {
            animation: none;
          }
        }
      `}</style>
    </>
  );
}

export default AboutPage;
